import React, { useState, useEffect } from 'react';
import { X, Plus, Check, Users, AlertCircle } from 'lucide-react';
import { useAuth } from '../../../App'; // Import from App.js
import { useTranslation } from '../../../contexts/LanguageContext';

const CreateSpaceForm = ({
  spaceName,
  setSpaceName,
  spaceDescription,
  setSpaceDescription,
  userSubscriptions,
  selectedSubscriptions,
  handleSubscriptionToggle,
  friends,
  selectedFriends,
  handleFriendToggle,
  inviteEmails,
  handleEmailChange,
  addEmailField,
  removeEmailField,
  handleSubmit,
  isLoading,
  onCancel
}) => {
  const { t } = useTranslation();
  const { httpClient } = useAuth(); // Get httpClient from auth context
  
  const [blockedMerchantIds, setBlockedMerchantIds] = useState([]);
  
  useEffect(() => {
    const fetchBlockedSubscriptions = async () => {
      if (!httpClient) return;
      
      try {
        const response = await httpClient.request('/api/user/subscriptions-in-other-spaces');
        
        if (response.ok) {
          const merchantIds = await response.json();
          setBlockedMerchantIds(merchantIds);
        }
      } catch (error) {
        console.error('Error fetching blocked subscriptions:', error);
      }
    };
    
    fetchBlockedSubscriptions();
  }, [httpClient]);

  const availableSubscriptions = userSubscriptions.filter(
    sub => !blockedMerchantIds.includes(sub.merchant_id)
  );
  
  const blockedSubscriptions = userSubscriptions.filter(
    sub => blockedMerchantIds.includes(sub.merchant_id)
  );

  return (
    <div className="mt-8 p-6 bg-blue-50 rounded-lg border border-blue-200">
      <h3 className="text-lg font-semibold mb-4 text-gray-800">
        {t('sharesubs.createSpaceForm.title')}
      </h3>
      <form onSubmit={handleSubmit}>
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('sharesubs.createSpaceForm.spaceName')}
          </label>
          <input
            type="text"
            value={spaceName}
            onChange={(e) => setSpaceName(e.target.value)}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder={t('sharesubs.createSpaceForm.spaceNamePlaceholder')}
            required
          />
        </div>
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('sharesubs.createSpaceForm.description')}
          </label>
          <textarea
            value={spaceDescription}
            onChange={(e) => setSpaceDescription(e.target.value)}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder={t('sharesubs.createSpaceForm.descriptionPlaceholder')}
            rows={2}
          />
        </div>

        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-3">
            {t('sharesubs.createSpaceForm.selectSubscriptions')}
          </label>
          
          {availableSubscriptions.length === 0 && blockedSubscriptions.length === 0 ? (
            <div className="text-center py-4 text-gray-500">
              {t('sharesubs.createSpaceForm.noSubscriptions')}
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                {availableSubscriptions.map(subscription => (
                  <div 
                    key={subscription.id}
                    onClick={() => handleSubscriptionToggle(subscription.id)}
                    className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                      selectedSubscriptions.includes(subscription.id)
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        {subscription.logo ? (
                          <img 
                            src={subscription.logo} 
                            alt={subscription.name}
                            className="w-8 h-8 rounded mr-3 object-contain"
                          />
                        ) : (
                          <div className="w-8 h-8 rounded mr-3 bg-gray-200 flex items-center justify-center">
                            <Users className="h-4 w-4 text-gray-400" />
                          </div>
                        )}
                        <div>
                          <div className="font-medium text-gray-800">{subscription.name}</div>
                          <div className="text-xs text-gray-500">
                            {subscription.price} {subscription.currency} / {subscription.billing_interval}
                          </div>
                        </div>
                      </div>
                      {selectedSubscriptions.includes(subscription.id) && (
                        <Check className="h-5 w-5 text-blue-500" />
                      )}
                    </div>
                  </div>
                ))}
              </div>
              
              {blockedSubscriptions.length > 0 && (
                <div className="mt-4">
                  <div className="flex items-center text-sm font-medium text-yellow-700 mb-2">
                    <AlertCircle className="h-4 w-4 mr-1" />
                    {t('sharesubs.createSpaceForm.alreadyInOtherSpaces')}
                  </div>
                  <div className="border border-yellow-200 rounded-lg bg-yellow-50 p-2">
                    {blockedSubscriptions.map(subscription => (
                      <div 
                        key={subscription.id}
                        className="p-3 border-b border-yellow-100 last:border-b-0 flex items-center opacity-70"
                      >
                        <div className="flex items-center flex-1">
                          {subscription.logo ? (
                            <img 
                              src={subscription.logo} 
                              alt={subscription.name}
                              className="w-6 h-6 rounded mr-2 object-contain"
                            />
                          ) : (
                            <div className="w-6 h-6 rounded mr-2 bg-gray-200 flex items-center justify-center">
                              <Users className="h-3 w-3 text-gray-400" />
                            </div>
                          )}
                          <div>
                            <div className="font-medium text-sm">{subscription.name}</div>
                          </div>
                        </div>
                        <div className="text-xs text-yellow-700 italic">
                          {t('sharesubs.createSpaceForm.alreadyInOtherSpace')}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-3">
            {t('sharesubs.createSpaceForm.inviteFriends')}
          </label>
          
          {friends.length === 0 ? (
            <div className="text-center py-4 text-gray-500">
              {t('sharesubs.createSpaceForm.noFriends')}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {friends.map(friend => (
                <div 
                  key={friend.userId}
                  onClick={() => handleFriendToggle(friend)}
                  className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                    selectedFriends.some(f => f.userId === friend.userId)
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center">
                    <div className="bg-gray-200 border-2 border-dashed rounded-xl w-10 h-10 flex items-center justify-center mr-3">
                      <Users className="h-5 w-5 text-gray-500" />
                    </div>
                    <div>
                      <div className="font-medium text-gray-800">{friend.firstName} {friend.lastName}</div>
                      <div className="text-xs text-gray-500">{friend.email}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-3">
            {t('sharesubs.createSpaceForm.inviteByEmail')}
          </label>
          
          {inviteEmails.map((email, index) => (
            <div key={index} className="flex items-center mb-2">
              <div className="relative flex-1">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => handleEmailChange(index, e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder={t('sharesubs.createSpaceForm.emailPlaceholder')}
                />
              </div>
              {inviteEmails.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeEmailField(index)}
                  className="ml-2 p-2 text-gray-500 hover:text-red-500"
                >
                  <X className="h-5 w-5" />
                </button>
              )}
              {index === inviteEmails.length - 1 && (
                <button
                  type="button"
                  onClick={addEmailField}
                  className="ml-2 p-2 text-blue-500 hover:text-blue-700"
                >
                  <Plus className="h-5 w-5" />
                </button>
              )}
            </div>
          ))}
        </div>

        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100"
          >
            {t('sharesubs.actions.cancel')}
          </button>
          <button
            type="submit"
            className={`px-4 py-2 rounded-lg ${
              selectedSubscriptions.length === 0
                ? 'bg-gray-300 cursor-not-allowed'
                : 'bg-[#253985] text-white hover:bg-[#1a2c6d]'
            }`}
            disabled={selectedSubscriptions.length === 0 || isLoading}
          >
            {isLoading ? t('sharesubs.createSpaceForm.creating') : t('sharesubs.actions.createSpace')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateSpaceForm;

// WORIKING VERISION WITH PLAN_ID and MERCHANT_ID
// TO FIX -> Flag subscriptions that are already in other spaces